import React, { FC, useMemo } from 'react';
import { Currency } from 'dinero.js';

import {
	TransformedSuperGridColDef, VerticalSuperGridColDef, Tooltip,
	NoValuePlaceholder,
} from '@aleph/shared-components';
import { RedditLevel } from 'wb-reddit/src/app/routes/superGrid/utils';
import StatCellRenderer from 'wb-reddit/src/app/routes/superGrid/cellRenderers/stat';
import { ICellRendererParams } from 'ag-grid-community';

interface IMoneyCellRendererParams extends ICellRendererParams {
	skipCurrency: boolean
	divisor?: number
	forcedCurrency?: Currency
	fallbackValue?: string
	disabled?: boolean
	appendedValue?: { value: string, tooltip?: string }
	placeholder?: string
}

const MoneyCellRenderer: FC<IMoneyCellRendererParams> = ({
	divisor,
	placeholder,
	...params
}) => {
	const colDef = params.colDef as TransformedSuperGridColDef<RedditLevel>;
	const isDashboardView = params.data?.isDashboardView && !params.node.expanded;
	const value = params.value ? Number(params.value) / (divisor || 1) : 0;

	const formattedValue = useMemo(() => {
		if (params.value == null || params.value === '') return null;
		const num = value;
		if (!Number.isFinite(num)) return null;

		return Number.isInteger(num) ? String(num) : num.toFixed(2);
	}, [params.value, value]);

	if (
		(params.disabled)
		|| (colDef?.level && colDef?.level !== params.data?.level)
		|| (params.data.supergridIsNoDataRow && !params.data.supergridIsCreateRow)
	) {
		return <NoValuePlaceholder />;
	}

	const derivedCurrency = (params?.colDef as VerticalSuperGridColDef<RedditLevel>)?.entityData?.currency
		?? params.data?.currency
		?? (params?.colDef as VerticalSuperGridColDef<RedditLevel>)?.entityData?.parent?.currency
		?? params.data?.parent?.currency;

	return (
		<>
			<div className="flex gap-2 px-[8px] h-[64px] max-h-full justify-between items-center">
				<div className="text-gray-600 text-[12px]">
					{params.skipCurrency ? '' : (params.forcedCurrency ?? derivedCurrency)}
				</div>
				<div className="flex gap-[8px]">
					{formattedValue ?? placeholder ?? ''}

					{
						params.appendedValue
						&& <Tooltip
							placement="top"
							content={params.appendedValue.tooltip}
						>
							<div className="text-gray-600 text-[12px]">{params.appendedValue.value}</div>
						</Tooltip>
					}
				</div>
			</div>
			{
				isDashboardView
				&& <StatCellRenderer {...params} />
			}
		</>
	);
};

export default MoneyCellRenderer;
